/**
 * useAffiliatedHotspots — F-034 / S10-01
 *
 * Hook for fetching affiliate hotspots (hotspots shared with the current user via shares).
 * Uses GET /api/v2/hotspots/affiliated which returns affiliated hotspot objects
 * with owner_name, share_pct, and earnings calculations.
 *
 * S10-01: Affiliated Hotspots Tab Feature-Parity
 */
import { useApi } from '../../lib/use-api'
import apiClient from '../../lib/api-client'
import type { Hotspot, HotspotsResponse } from './useHotspots'

// --- Types ---

// AffiliatedHotspot response from API (new v2 endpoint)
type AffiliatedHotspotResponse = {
  id: number
  name: string
  owner_name: string
  share_pct: number
  affiliated_earnings_7d: number
  affiliated_earnings_30d: number
  era: 'post' | 'pre'
  status: 'online' | 'offline'
  city_name?: string | null
  country?: string | null
  address: string
}

// API Response wrapper from /api/v2/hotspots/affiliated
type AffiliatedHotspotsApiResponse = {
  pagination: {
    current: number
    per_page: number
    pages: number
    count: number
  }
  data: AffiliatedHotspotResponse[]
}

// --- Transform Helper ---

function transformAffiliatedHotspotToHotspot(ah: AffiliatedHotspotResponse): Hotspot {
  return {
    id: String(ah.id),
    name: ah.name,
    address: ah.address,
    status: ah.status === 'online' ? 'online' : ah.status === 'offline' ? 'offline' : 'warn',
    online_pct: 0, // Not provided by affiliated endpoint
    hnt_24h: 0, // Not provided by affiliated endpoint, use earnings data if needed
    hnt_7d: ah.affiliated_earnings_7d,
    hnt_30d: ah.affiliated_earnings_30d,
    device_type: 'unknown', // Not provided by affiliated endpoint
    maker: '', // Not provided by affiliated endpoint
    city: ah.city_name ? { name: ah.city_name } : null,
    era: ah.era,
    lat: undefined,
    lng: undefined,
    sparkline_data: undefined,
  }
}

// --- Hook ---

type UseAffiliatedHotspotsResult = {
  data: Hotspot[] | undefined
  // Issue #57: gleiche Meta-Form wie useHotspots, damit HotspotsList beide
  // Quellen ohne Sonderfall lesen kann (plan_limit hier nie gesetzt).
  meta: HotspotsResponse['meta'] | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useAffiliatedHotspots(enabled = true): UseAffiliatedHotspotsResult {
  const query = useApi<AffiliatedHotspotsApiResponse>(
    ['affiliated-hotspots'],
    async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = apiClient as any
      const result = await client.GET('/api/v2/hotspots/affiliated', {
        params: {
          query: { per_page: 500 },
        },
      })
      return result as { data?: AffiliatedHotspotsApiResponse; error?: unknown }
    },
    // rules-of-hooks: Aufrufer schalten per enabled statt Hooks bedingt aufzurufen.
    { enabled },
  )

  return {
    data: query.data?.data?.map(transformAffiliatedHotspotToHotspot),
    meta: query.data?.pagination
      ? {
          pagy: {
            page: query.data.pagination.current,
            pages: query.data.pagination.pages,
            count: query.data.pagination.count,
            items: query.data.pagination.per_page,
          },
        }
      : undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
